import opentype, { type Font } from 'opentype.js';
export const fontFiles = {
  Basic: 'Basic-Regular.ttf',
  Fondamento: 'Fondamento-Regular.ttf',
  VT323: 'VT323-Regular.ttf',
  CinzelDecorative: 'CinzelDecorative-Regular.ttf',
  BebasNeue: 'BebasNeue-Regular.ttf',
  Poppins: 'Poppins-Regular.ttf',
  Lato: 'Lato-Regular.ttf',
  FiraSans: 'FiraSans-Regular.ttf',
  Ubuntu: 'Ubuntu-Regular.ttf',
  IBMPlexSerif: 'IBMPlexSerif-Regular.ttf',
  IBMPlexMono: 'IBMPlexMono-Regular.ttf',
  IntelOneMono: 'IntelOneMono-Regular.ttf',
  AtkinsonHyperlegible: 'AtkinsonHyperlegible-Regular.ttf'
};
export const fonts: Partial<Record<string, Font>> = {};
export function registerFont(name: string, buffer: ArrayBuffer) { fonts[name] = opentype.parse(buffer); }

export async function ensureGoogleFontLoaded(name: string): Promise<void> {
  if (typeof document === 'undefined' || !name) return;
  const id = 'cr-gf-' + name.toLowerCase().replace(/[^a-z0-9]/g, '-');
  if (!document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(name).replace(/%20/g, '+')}&display=swap`;
    document.head.appendChild(link);
  }
  if (document.fonts?.load) {
    try {
      await document.fonts.load(`16px "${name}"`);
    } catch {}
  }
}

/** Identical font outlines are used by the browser and production canvas. */
export function textPath(text: string, name: string, curve = 0) {
  const font = fonts[name];
  if (!font) throw new Error(`Font ${name} is not loaded.`);
  for (const char of text.replace(/\s/g, '')) if (!font.charToGlyph(char).index) throw new Error(`The ${name} font does not support “${char}”. Choose another character.`);
  const path = new opentype.Path();
  const lines = text.split('\n');
  if (lines.length > 4) throw new Error('Use no more than four text lines.');
  if (curve && lines.length > 1) throw new Error('Curved text must fit on one line.');
  lines.forEach((line, index) => {
    const advance = font.getAdvanceWidth(line, 1000);
    if (!curve) { path.extend(font.getPath(line, -advance/2, index*1250, 1000).commands); return; }
    const angle = curve * Math.PI/180, radius = Math.max(1, advance) / angle;
    font.forEachGlyph(line, 0, 0, 1000, {}, (glyph, x) => {
      const a = (x - advance/2) / radius;
      const tx = radius*Math.sin(a), ty = radius*(1-Math.cos(a));
      const gp = glyph.getPath(0, 0, 1000);
      for (const command of gp.commands) {
        const c = { ...command } as any;
        for (const [kx,ky] of [['x','y'],['x1','y1'],['x2','y2']]) if (typeof c[kx] === 'number') {
          const ox = c[kx], oy = c[ky]; c[kx] = tx + ox*Math.cos(a)-oy*Math.sin(a); c[ky] = ty + ox*Math.sin(a)+oy*Math.cos(a);
        }
        path.commands.push(c);
      }
    });
  });
  const b = path.getBoundingBox();
  return { data: path.toPathData(3), x: b.x1, y: b.y1, width: Math.max(1,b.x2-b.x1), height: Math.max(1,b.y2-b.y1) };
}
